/**
 * KEY-743: whether to ask "is this the right name for your organisation?", and remembering a "Not now".
 *
 * A self-serve purchase names the buyer's organisation from their email address ("Acme" from sarah@acme.com,
 * "Sarah" from sarah@gmail.com) because it must never fail for want of a name. The server works out whether the
 * name is still that guess and whether this person could change it (`me.organisationNameIsGuessed`); all this
 * decides is whether they have already said "Not now" to it for that organisation, in this browser.
 *
 * Keyed by organisation, not by person: a second organisation with a guessed name deserves its own question.
 * Renaming ends the prompt for good on its own — the server stops saying the name is guessed.
 *
 * Every storage access is wrapped: a blocked or full store must never take the page down.
 */
import type { MeResponse } from "@keydials/api-client";

export const ORG_NAME_DISMISSED_KEY_PREFIX = "kde.org-name-prompt-dismissed.v1.";

type PromptStorage = Pick<Storage, "getItem" | "setItem">;

const keyFor = (organisationId: string) => `${ORG_NAME_DISMISSED_KEY_PREFIX}${organisationId}`;

function defaultStorage(): PromptStorage | null {
  try {
    return localStorage;
  } catch {
    return null;
  }
}

export function isOrgNamePromptDismissed(
  organisationId: string,
  storage: PromptStorage | null = defaultStorage(),
): boolean {
  try {
    return storage?.getItem(keyFor(organisationId)) === "1";
  } catch {
    return false;
  }
}

export function dismissOrgNamePrompt(organisationId: string, storage: PromptStorage | null = defaultStorage()): void {
  try {
    storage?.setItem(keyFor(organisationId), "1");
  } catch {
    // Storage full, blocked, or disabled. The prompt returns next load; a crash would be worse.
  }
}

/**
 * True when the server says the organisation's name is still the guess (it already folds in the flag, org.edit,
 * the organisation type and kiosk/guest/impersonated sessions) and nobody has said "Not now" to it here.
 */
export function shouldAskOrgName(
  me: Pick<MeResponse, "organisationId" | "organisationName" | "organisationNameIsGuessed"> | null | undefined,
  storage: PromptStorage | null = defaultStorage(),
): boolean {
  if (!me?.organisationNameIsGuessed || !me.organisationId || !me.organisationName) return false;
  return !isOrgNamePromptDismissed(me.organisationId, storage);
}
